import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function InviteCandidateDialog({ open, onOpenChange, candidate }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/jobs/mine")
      .then(({ data }) => {
        const active = (data || []).filter(j => j.is_active);
        setJobs(active);
        if (active.length === 1) setJobId(active[0].job_id);
      })
      .catch(() => {});
  }, [open]);

  const send = async () => {
    if (!jobId) { toast.error("Pick a job first"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/employer/invite-candidate", {
        candidate_user_id: candidate.user_id,
        job_id: jobId,
        message: message.trim() || undefined,
      });
      toast.success(data.email_sent ? `Invitation sent to ${candidate.name}` : "Invitation logged (email queued)");
      onOpenChange(false);
      setMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to invite");
    } finally {
      setSending(false);
    }
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite {candidate.name} to apply</DialogTitle>
          <DialogDescription>
            We'll email {candidate.name} from QaTestology with the job details and a direct apply link.
            Replies go straight to your inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Open role *</Label>
            <select
              value={jobId}
              onChange={(e)=>setJobId(e.target.value)}
              data-testid="invite-job-select"
              className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
            >
              <option value="">Select a job…</option>
              {jobs.map(j => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
            </select>
            {jobs.length === 0 && (
              <p className="mt-1.5 text-xs text-amber-600">You don't have any active jobs yet. Post one first.</p>
            )}
          </div>
          <div>
            <Label>Personal note (optional)</Label>
            <Textarea
              value={message}
              onChange={(e)=>setMessage(e.target.value)}
              rows={4}
              placeholder="Hey, I came across your QaTestology profile and your Playwright background is exactly what we need…"
              data-testid="invite-message"
              className="mt-1.5"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="invite-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending || !jobId} data-testid="invite-send" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
